import React from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { AuditButton, renderOcplcSelectHeader } from 'pages/ocplcDashboard/ocplcHelpers';  
import RenderTextfieldHeader from '../../renderTextFieldHeader';
import FeedIcon from '@mui/icons-material/Feed';
import EditIcon from '@mui/icons-material/Edit';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { colorTypes, columnTypes, dateConfig, regionalFields, renderDateCell, renderTextCell } from './columnTypes';
import { VERSION_0002 } from 'constants/ocplcConstants';

export const getModelColumns = (filters, modelAvailabilityData = [], handleChange, countryList, businessModels, regionDropdown, handleViewModel, region, disableAction, handleFeedDialog) => {
    const isFieldNotEmpty = (field) => modelAvailabilityData.length > 0 && modelAvailabilityData.every(row => row[field]);
    const { startDateLabel, endDateLabel, startDateValue, endDateValue } = dateConfig[region] || dateConfig.default;
    let regionCheckBoxColumns = []
    const version = modelAvailabilityData?.[0]?.version;

    const auditButtonsConfig = [
        {
            show: region !== "WW",
            title: "Marketing Program Audit Trial",
            icon: <TrackChangesIcon />,
            dialogType: version === VERSION_0002 ? "modelTargetOfferAudit" : "modelTargetOfferAuditV3"
        },
        {
            show: true,
            title: "Audit Trail - Model",
            icon: <FeedIcon />,
            dialogType: version === VERSION_0002 ? "modelOfferAudit" : "modelOfferAuditV3"
        },
    ]

    // Common columns for all regions
    const baseColumns = [
        {
            headerName: 'Product Family', field: 'productFamily', width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderTextCell(params.row.productFamily, colorTypes[params.row.status] || 'grey'),
            renderHeader: () => <RenderTextfieldHeader colData={columnTypes.productFamily} label="Product Family" filters={filters} handleChange={handleChange} />
        },
        {
            headerName: 'Product Description', field: 'productDesc', width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderTextCell(params.row.productDesc),
            renderHeader: () => <RenderTextfieldHeader colData={columnTypes.productDesc} label="Product Description" filters={filters} handleChange={handleChange} />
        },
        {
            headerName: 'Region', field: 'region', width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderTextCell(params.row.region),
            renderHeader: () => renderOcplcSelectHeader(columnTypes.region, filters, 'Region', handleChange, regionDropdown)
        },
        {
            headerName: 'Country ID', field: 'country', width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderTextCell(params.row.country),
            renderHeader: () => renderOcplcSelectHeader(columnTypes.country, filters, 'Country ID', handleChange, countryList)
        },
        {
            headerName: 'Country Description', field: 'countryDesc', width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderTextCell(params.row.countryDesc),
            renderHeader: () => <RenderTextfieldHeader colData={columnTypes.countryDesc} label="Country Description" filters={filters} handleChange={handleChange} />
        },
        {
            headerName: 'Business Model', field: 'businessModel', width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderTextCell(params.row.businessModel),
            renderHeader: () => renderOcplcSelectHeader(columnTypes.businessModel, filters, 'Business Model', handleChange, businessModels)
        },
        {
            headerName: 'Status', field: 'status', width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderTextCell(params.row.status),
            renderHeader: () => <RenderTextfieldHeader colData={columnTypes.status} label="Status" filters={filters} handleChange={handleChange} />
        },
        {
            headerName: 'Start Date', field: 'startDate', width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderDateCell(params.row[startDateValue]),
            renderHeader: () => <RenderTextfieldHeader colData={columnTypes[startDateValue]} label={startDateLabel} filters={filters} handleChange={handleChange} />
        },
        region !== "WW" && {
            headerName: 'Regional Start Date', field: 'regStartDate', width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderDateCell(params.row.startDate),
            renderHeader: () => <RenderTextfieldHeader colData={columnTypes.startDate} label="Start Date" filters={filters} handleChange={handleChange} />
        },
        region !== "WW" && {
            headerName: 'Regional End Date', field: 'regEndDate', width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderDateCell(params.row.endDate),
            renderHeader: () => <RenderTextfieldHeader colData={columnTypes.endDate} label="End Date" filters={filters} handleChange={handleChange} />
        },
        {
            headerName: 'End Date', field: 'endDate', width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderDateCell(params.row[endDateValue]),
            renderHeader: () => <RenderTextfieldHeader colData={columnTypes[endDateValue]} label={endDateLabel} filters={filters} handleChange={handleChange} />
        },
    ].filter(Boolean);

    // Regional Dates Columns
    const regionalDateColumns = regionalFields.flatMap(({ startField, startName, endField, endName }) => [
        isFieldNotEmpty(startName) && {
            headerName: startName, field: startName, width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderDateCell(params.row[startField]),
            renderHeader: () => <RenderTextfieldHeader colData={columnTypes[startField]} label={modelAvailabilityData?.[0]?.[startName]} filters={filters} handleChange={handleChange} />
        },
        isFieldNotEmpty(endName) && {
            headerName: endName, field: endName, width: 155, sortable: false, headerClassName: 'dashboard-data-grid-headers',
            renderCell: (params) => renderDateCell(params.row[endField]),
            renderHeader: (params) => <RenderTextfieldHeader colData={columnTypes[endField]} label={modelAvailabilityData?.[0]?.[endName]} filters={filters} handleChange={handleChange} />
        },
    ]).filter(Boolean);

    const authorColumn = {
        headerName: 'Who Changed', field: 'whoChanged', width: 220, sortable: false, headerClassName: 'dashboard-data-grid-headers',
        renderCell: (params) => renderTextCell(params.row.whoChanged),
        renderHeader: () => <RenderTextfieldHeader colData={columnTypes.whoChanged} label="Author" filters={filters} handleChange={handleChange} />
    };

    // WW Region Columns
    if (region === 'WW')
        regionCheckBoxColumns = ['apRegion', 'euRegion', 'laRegion', 'naRegion'].map(regionField => ({
            headerName: regionField.slice(0, 2).toUpperCase(),
            headerClassName: 'dashboard-data-grid-checkbox-headers',
            field: regionField, width: 40, sortable: false,
            renderCell: (params) => <Checkbox disabled checked={params.row[regionField] === true} />,
        }))

    const actionColumn = {
        headerName: '', field: 'actions', width: 180, sortable: false,
        renderCell: (params) => (
            <Box onClick={(e) => e.stopPropagation()}>
                {auditButtonsConfig.map((btn, index) => (
                    <AuditButton
                        key={btn.dialogType}
                        show={btn.show}
                        title={btn.title}
                        onClick={() => handleFeedDialog(params.row, btn.dialogType)}
                        icon={btn.icon}
                        dialogType={btn.dialogType}
                    />
                ))}
                <Tooltip title={disableAction ? "View" : "Edit"}>
                    <Button onClick={() => { handleViewModel(params.row) }} startIcon={disableAction ? <VisibilityIcon /> : <EditIcon />} size="medium" />
                </Tooltip>
            </Box>
        ),
    };

    return [...baseColumns, ...regionalDateColumns, authorColumn, ...regionCheckBoxColumns, actionColumn];
};